import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import ConditionalWarningPopupButton from 'components/molecules/buttons/conditional-warning-popup-button/ConditionalWarningPopupButton';
import { Form } from 'components/organisms';
import { trackInitialValues, TrackTabs } from 'components/organisms/forms/track/TrackForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { updateMusicProdoctDigitalAlbumsFromTrackResponse } from 'middleware/music-product-digital/updateMusicProdoctDigitalAlbumsFromTrackResponse';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetTrack, usePutTrack } from 'services/tracks';
import {
  useDeleteTrackFile,
  usePostTrackChunkedFile,
  usePostTrackFile,
  usePutTrackChunkedFile,
  usePutTrackFile,
} from 'services/tracks/file';
import { ITrackFilePath, ITrackMutationForm, ITrackResponse } from 'services/tracks/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditTrack = () => {
  const trackId = useParamId('track');
  const { pathname } = useLocation();

  const { musicProductDigitalForm, clearForm, clearForms, setForm, trackForm } = useStoreState();

  const { t } = useTranslation();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const getTrack = useGetTrack(trackId);

  const updateTrack = usePutTrack();

  const addTrackFile = usePostTrackFile();
  const addTrackChunkFile = usePostTrackChunkedFile();
  const updateTrackFile = usePutTrackFile(trackId);
  const updateTrackChunkFile = usePutTrackChunkedFile();
  const deleteTrackFile = useDeleteTrackFile(trackId);

  const mutateOptions = useMutateOptions<ITrackMutationForm>();

  const onGoBack = async (track?: ITrackResponse) => {
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.tracks],
    });
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.tracksLightList],
    });
    if (musicProductDigitalForm) {
      if (track) {
        setForm(
          'musicProductDigitalForm',
          updateMusicProdoctDigitalAlbumsFromTrackResponse(track, musicProductDigitalForm),
        );
      }
      navigate(
        `/music-product-digital/${musicProductDigitalForm.id.length ? `edit/${musicProductDigitalForm.id}` : 'create'}/tracks`,
        { replace: true },
      );
      clearForm('trackForm');

      return;
    }
    navigate(`/track/${trackId}/${getLastPathname(pathname)}`, { replace: true });
    clearForms();
  };

  const onTrackFileMutationSuccess = async (
    formikHelpers: FormikHelpers<ITrackMutationForm>,
    value?: ITrackFilePath,
  ) => {
    formikHelpers.setFieldValue('mp3Link', value?.mp3Link);
    formikHelpers.setFieldValue('mp3PreviewLink', value?.mp3PreviewLink);
    formikHelpers.setFieldValue('wavLink', value?.wavLink);
    formikHelpers.setFieldValue('file', undefined);
  };

  const areFileLinksExists = (wavLink?: string, mp3Link?: string, mp3PreviewLink?: string) =>
    wavLink?.length && mp3Link?.length && mp3PreviewLink?.length;

  const onEditTrack = async (
    values: ITrackMutationForm,
    formikHelpers: FormikHelpers<ITrackMutationForm>,
  ) => {
    const initialValues = getTrack.data && middleware.track.queryToForm(getTrack.data);
    let trackVersion = values.version;
    if (initialValues) {
      const { wavLink, mp3Link, mp3PreviewLink } = initialValues;

      if (values.file && !areFileLinksExists(wavLink, mp3Link, mp3PreviewLink)) {
        await addTrackChunkFile.mutateAsync(
          { file: values.file, trackId, version: values.version },
          {
            onError: (error) => onErrorShowToast(error, 'toast-message.add-file'),
            onSuccess: (value) => {
              trackVersion = value.version;
              onTrackFileMutationSuccess(formikHelpers, value);
            },
          },
        );
      }

      if (values.file && areFileLinksExists(wavLink, mp3Link, mp3PreviewLink)) {
        await updateTrackChunkFile.mutateAsync(
          { file: values.file, trackId, version: values.version },
          {
            onError: (error) => onErrorShowToast(error, 'toast-message.change-file'),
            onSuccess: (value) => {
              trackVersion = value.version;
              onTrackFileMutationSuccess(formikHelpers, value);
            },
          },
        );
      }

      if (
        !values.file &&
        !areFileLinksExists(wavLink, mp3Link, mp3PreviewLink) &&
        areFileLinksExists(
          getTrack.data?.wavLink,
          getTrack.data?.mp3Link,
          getTrack.data?.mp3PreviewLink,
        )
      ) {
        await deleteTrackFile.mutateAsync(undefined, {
          onError: (error) => onErrorShowToast(error, 'toast-message.delete-file'),
          onSuccess: (value) => {
            trackVersion = value.version;
            onTrackFileMutationSuccess(formikHelpers);
          },
        });
      }
    }

    await updateTrack.mutateAsync(
      { ...middleware.track.formToQuery(values), trackId: trackId, version: trackVersion },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: async (track) => {
          onGoBack(track);
          await queryClient.invalidateQueries({
            queryKey: [listQueryKeys.tracks],
          });
        },
      },
    );
  };

  const trackEditInitialValues = useMemo(() => {
    if (!getTrack.data) return trackInitialValues;

    if (trackForm)
      return {
        ...trackForm,
        releaseStatus: getTrack.data.trackStatus,
      };

    return middleware.track.queryToForm(getTrack.data);
  }, [trackForm, getTrack.data]);

  return (
    <Form.Track
      i18nextPrefix="track"
      initialValues={trackEditInitialValues}
      disableTabs={[TrackTabs.contracts]}
      tittle={getMainTitle(getTrack.data?.mainTitle)}
      status={{ isLoading: getTrack.isLoading }}
      customBackItem={
        <ConditionalWarningPopupButton
          title={t('popup.form-cancelation-title')}
          message={t('popup.form-cancelation-description')}
          confirmButtonVariant={BUTTON_VARIANT.background}
          showWarning={!musicProductDigitalForm}
          onConfirm={() => onGoBack()}
          isClear
          onlyIcon
          disabled={!getTrack.data}
        >
          <Button.Icon icon={<PiX />} />
        </ConditionalWarningPopupButton>
      }
      onSubmit={onEditTrack}
    />
  );
};

export default EditTrack;
